import { isEmpty } from 'lodash';
import { createSelector } from 'reselect';
import { EXTEND_HIERARCHY_ITEMS, RETRIEVE_HIERARCHY_ITEMS, RETRIEVE_HIERARCHY_ITEMS_FAILURE, RETRIEVE_HIERARCHY_ITEMS_SUCCESS, RETRIEVE_HIERARCHY_ROOT, RETRIEVE_HIERARCHY_ROOT_FAILURE, RETRIEVE_HIERARCHY_ROOT_SUCCESS } from 'Redux/actionTypes';
import { definePathArray, getDescendantTree, getHierarchiesPathsMap, nodeByDescriptionSortFn } from 'Utils/hierarchy/helpers';
// ------------------------------------
// Constants
// ------------------------------------
export const initialState = {
    isLoadingItems: false,
    timestampItems: null,
    hierarchyMap: {},
    retrieveItemsError: null,
    hierarchiesPathsMap: {},
    isLoadingRoot: false,
    timestampRoot: null,
    hierarchyRootItemsIds: [],
    retrieveRootError: null,
    hierarchyRootItemsNodes: []
};
// ------------------------------------
// Selectors
// ------------------------------------
/**
 * Simple function that takes Redux Store object and returns
 * subset of data that represents redux business-logic module slice
 * Beneficial because it encapsulates knowledge of
 * where to find that particular subset
 * @function
 * @param {Object} state redux Store object
 * @returns {Object} subset of data
 */
export const hierarchyModuleSelector = (state) => state.hierarchyModule;
/**
 * Selector function that returns hierarchy items hash loading state
 * @param {Object} state
 * @returns {Boolean} is loading
 */
export const hierarchyMapIsLoadingSelector = (state) => hierarchyModuleSelector(state).isLoadingItems;
/**
 * Selector function that returns all hierarchy items hash
 * depends on provided hierarchy modes for nodes/locations (active models, with events)
 * @param {Object} state
 * @returns {Object} items hash filtered on the backend by modes
 */
export const hierarchyMapSelector = (state) => hierarchyModuleSelector(state).hierarchyMap;
/**
 * Selector function that returns all root items ids defined for the hierarchy
 * doesn't relay on provided hierarchy modes for nodes/locations (active models, with events)
 * to get valid nodes that depends on modes use hierarchyValidRootItemsIdsSelector()
 * @param {Object} state
 * @returns {Array} all root items ids
 */
export const hierarchyRootItemsIdsSelector = (state) => hierarchyModuleSelector(state).hierarchyRootItemsIds;
/**
 * Selector function that returns hierarchy root item nodes
 * @param {Object} state
 * @returns {Array} root item nodes
 */
export const hierarchyRootItemNodesSelector = (state) => hierarchyModuleSelector(state).hierarchyRootItemsNodes;
/**
 * Selector function to get node from items hash by its id
 * @param {Object} state
 * @param {string} id node id
 * @returns {Object||Null} node
 */
export const hierarchyNodeByIdSelector = (id) => (state) => {
    const hash = hierarchyMapSelector(state);
    return hash?.[id] ?? null;
};
/**
 * Selector function that returns list of items that are present in items hash
 * so it is ids of valid items, already sorted by description
 * @param {Object} state
 * @param {Array} itemsIds list of any nodes items ids
 * @returns {Array} valid and sorted, items ids
 */
export const hierarchyValidItemsIdsSelector = (itemsIds) => (state) => {
    const itemsNodes = itemsIds.map((id) => hierarchyNodeByIdSelector(id)(state));
    const validItemsNodesSorted = itemsNodes
        .reduce((acc, node) => {
        if (node)
            acc.push(node);
        return acc;
    }, [])
        .sort(nodeByDescriptionSortFn);
    return validItemsNodesSorted.map((node) => node.id);
};
/**
 * Selector function that returns list of root items that are present in items hash
 * so it is ids of valid items, already sorted by description
 * @param {Object} state
 * @returns {Array} valid and sorted, root items ids
 */
export const hierarchyValidRootItemsIdsSelector = createSelector(hierarchyRootItemsIdsSelector, hierarchyMapSelector, (allRootItemsIds, hierarchyMap) => {
    const itemsNodes = allRootItemsIds.map((id) => hierarchyMap?.[id] ?? null);
    const validItemsNodesSorted = itemsNodes
        .reduce((acc, node) => {
        if (node)
            acc.push(node);
        return acc;
    }, [])
        .sort(nodeByDescriptionSortFn);
    return validItemsNodesSorted.map((node) => node.id);
});
export const nodeDescendantTreeNodesByIdSelector = (id) => (state) => {
    try {
        const hash = hierarchyMapSelector(state);
        if (isEmpty(hash))
            return [];
        return getDescendantTree(hash, id);
    }
    catch (error) {
        console.error(error);
        return [];
    }
};
export const nodePathIdsByIdSelector = (id) => createSelector(hierarchyMapSelector, (hash) => {
    try {
        return definePathArray(hash, id);
    }
    catch (error) {
        return [];
    }
});
// ------------------------------------
// Reducer
// ------------------------------------
const hierarchyReducer = (state = initialState, action) => {
    switch (action.type) {
        case RETRIEVE_HIERARCHY_ITEMS:
            return {
                ...state,
                isLoadingItems: true
            };
        case RETRIEVE_HIERARCHY_ITEMS_SUCCESS: {
            const { items, timestamp } = action.payload;
            const hierarchiesPathsMap = getHierarchiesPathsMap(items);
            const { hierarchyRootItemsIds } = state;
            const hierarchyRootItemsNodes = hierarchyRootItemsIds.map((id) => items?.[id] ?? null);
            return {
                ...state,
                isLoadingItems: false,
                retrieveItemsError: null,
                timestampItems: timestamp,
                hierarchyMap: items,
                hierarchiesPathsMap,
                hierarchyRootItemsNodes
            };
        }
        case RETRIEVE_HIERARCHY_ITEMS_FAILURE:
            return {
                ...state,
                isLoadingItems: false,
                retrieveItemsError: action.payload.error,
                timestampItems: action.payload.timestamp
            };
        case EXTEND_HIERARCHY_ITEMS: {
            const { items } = action.payload;
            const extendedItems = { ...items, ...state.hierarchyMap };
            const extendedHierarchiesPathsMap = getHierarchiesPathsMap(extendedItems);
            return {
                ...state,
                hierarchyMap: extendedItems,
                hierarchiesPathsMap: extendedHierarchiesPathsMap
            };
        }
        case RETRIEVE_HIERARCHY_ROOT:
            return {
                ...state,
                isLoadingRoot: true
            };
        case RETRIEVE_HIERARCHY_ROOT_SUCCESS: {
            const { nodes, timestamp } = action.payload;
            return {
                ...state,
                isLoadingRoot: false,
                retrieveRootError: null,
                timestampRoot: timestamp,
                hierarchyRootItemsIds: nodes
            };
        }
        case RETRIEVE_HIERARCHY_ROOT_FAILURE:
            return {
                ...state,
                isLoadingRoot: false,
                retrieveRootError: action.payload.error,
                timestampRoot: action.payload.timestamp
            };
        default:
            return state;
    }
};
export default hierarchyReducer;
